import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Search, FileText, Send, Ban, IndianRupee, Eye, Download, Pencil, Trash2 } from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-muted text-muted-foreground',
  sent: 'bg-blue-100 text-blue-700',
  paid: 'bg-emerald-100 text-emerald-700',
  partially_paid: 'bg-amber-100 text-amber-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-muted text-muted-foreground',
};

const INITIAL_FORM = {
  client_id: '',
  invoice_date: new Date().toISOString().split('T')[0],
  due_date: '',
  base_amount: '',
  gst_type: 'igst',
  invoice_type: 'recruitment',
  description: 'Manpower Recruitment and supply agency services',
  is_manual_entry: false,
  manual_invoice_number: '',
  // Extended fields
  line_items: [{ _key: 1, description: 'Manpower Recruitment and supply agency services', candidate_name: '', date_of_joining: '', num_surveys: '', price: '', amount: '' }],
  sac_code: '998519',
  lut_arn_no: 'AD0603240203210',
  lut_arn_date: '12/03/2024',
  manual_igst: '',
  manual_cgst: '',
  manual_sgst: '',
  round_off: '0',
  amount_already_paid: '0',
};

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [useManualGST, setUseManualGST] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, clientRes] = await Promise.all([
        api.get('/finance/invoices'),
        api.get('/finance/clients'),
      ]);
      setInvoices(invRes.data);
      setClients(clientRes.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => invoices.filter(inv => {
    const matchSearch = inv.invoice_number.toLowerCase().includes(search.toLowerCase()) ||
                        inv.client_name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchSearch && matchStatus;
  }), [invoices, search, statusFilter]);

  const openAdd = () => {
    setForm(INITIAL_FORM);
    setEditMode(false);
    setEditingId(null);
    setUseManualGST(false);
    setDialogOpen(true);
  };

  const openEdit = async (invoice) => {
    try {
      const res = await api.get(`/finance/invoices/${invoice.id}`);
      const inv = res.data;
      
      setForm({
        client_id: inv.client_id,
        invoice_date: inv.invoice_date,
        due_date: inv.due_date,
        base_amount: inv.base_amount.toString(),
        gst_type: inv.gst_type,
        invoice_type: inv.invoice_type || 'recruitment',
        description: inv.description,
        is_manual_entry: inv.is_manual_entry,
        manual_invoice_number: inv.invoice_number,
        line_items: inv.line_items?.length > 0 ? inv.line_items.map((item, idx) => ({ ...item, _key: idx + 1 })) : [{ _key: 1, description: inv.description, candidate_name: '', date_of_joining: '', amount: inv.base_amount }],
        sac_code: inv.sac_code || '998519',
        lut_arn_no: inv.lut_arn_no || 'AD0603240203210',
        lut_arn_date: inv.lut_arn_date || '12/03/2024',
        manual_igst: inv.igst?.toString() || '',
        manual_cgst: inv.cgst?.toString() || '',
        manual_sgst: inv.sgst?.toString() || '',
        round_off: inv.round_off?.toString() || '0',
        amount_already_paid: inv.amount_paid?.toString() || '0',
      });
      
      setUseManualGST(inv.is_manual_entry);
      setEditMode(true);
      setEditingId(inv.id);
      setDialogOpen(true);
    } catch {
      toast.error('Failed to load invoice details');
    }
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setForm(prev => ({
      ...prev,
      client_id: clientId,
      gst_type: client?.state === 'Haryana' ? 'cgst_sgst' : 'igst',
      due_date: client ? calculateDueDate(prev.invoice_date, client.credit_period_days) : prev.due_date,
    }));
  };

  const calculateDueDate = (invoiceDate, creditDays) => {
    const date = new Date(invoiceDate);
    date.setDate(date.getDate() + creditDays);
    return date.toISOString().split('T')[0];
  };

  const updateLineItem = (index, field, value) => {
    setForm(prev => {
      const newItems = [...prev.line_items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      // Auto-calculate base amount from line items
      const totalAmount = newItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      
      return {
        ...prev,
        line_items: newItems,
        base_amount: totalAmount.toString(),
      };
    });
  };

  const addLineItem = () => {
    setForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, { _key: Date.now(), description: '', candidate_name: '', date_of_joining: '', num_surveys: '', price: '', amount: '' }],
    }));
  };

  const removeLineItem = (index) => {
    if (form.line_items.length === 1) return;
    setForm(prev => {
      const newItems = prev.line_items.filter((_, i) => i !== index);
      const totalAmount = newItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      return {
        ...prev,
        line_items: newItems,
        base_amount: totalAmount.toString(),
      };
    });
  };

  const handleSave = async () => {
    if (!form.client_id || !form.base_amount || !form.invoice_date || !form.due_date) {
      toast.error('Please fill all required fields');
      return;
    }
    if (form.is_manual_entry && !form.manual_invoice_number) {
      toast.error('Invoice number is required for manual entry');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        client_id: form.client_id,
        invoice_date: form.invoice_date,
        due_date: form.due_date,
        base_amount: parseFloat(form.base_amount),
        gst_type: form.gst_type,
        invoice_type: form.invoice_type || 'recruitment',
        description: form.description,
        is_manual_entry: form.is_manual_entry,
        manual_invoice_number: form.manual_invoice_number || null,
        placement_id: null,
        line_items: form.line_items.map((item, idx) => ({
          description: item.description,
          candidate_name: item.candidate_name || null,
          date_of_joining: item.date_of_joining || null,
          num_surveys: item.num_surveys ? parseInt(item.num_surveys) : null,
          price: item.price ? parseFloat(item.price) : null,
          quantity: 1,
          amount: parseFloat(item.amount) || 0,
        })),
        sac_code: form.sac_code || '998519',
        lut_arn_no: form.lut_arn_no || null,
        lut_arn_date: form.lut_arn_date || null,
        round_off: parseFloat(form.round_off) || 0,
        amount_already_paid: parseFloat(form.amount_already_paid) || 0,
      };
      
      // Add manual GST if enabled
      if (useManualGST || form.is_manual_entry) {
        if (form.manual_igst) payload.manual_igst = parseFloat(form.manual_igst);
        if (form.manual_cgst) payload.manual_cgst = parseFloat(form.manual_cgst);
        if (form.manual_sgst) payload.manual_sgst = parseFloat(form.manual_sgst);
      }
      
      if (editMode) {
        await api.put(`/finance/invoices/${editingId}`, {
          invoice_number: form.manual_invoice_number,
          invoice_date: form.invoice_date,
          due_date: form.due_date,
          base_amount: parseFloat(form.base_amount),
          gst_type: form.gst_type,
          description: form.description,
          line_items: payload.line_items,
          sac_code: form.sac_code,
          lut_arn_no: form.lut_arn_no,
          lut_arn_date: form.lut_arn_date,
          round_off: payload.round_off,
          ...(useManualGST && {
            manual_igst: parseFloat(form.manual_igst) || undefined,
            manual_cgst: parseFloat(form.manual_cgst) || undefined,
            manual_sgst: parseFloat(form.manual_sgst) || undefined,
          }),
        });
        toast.success('Invoice updated');
      } else {
        await api.post('/finance/invoices', payload);
        toast.success('Invoice created');
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const markAsSent = async (invoiceId) => {
    try {
      await api.put(`/finance/invoices/${invoiceId}/send`);
      toast.success('Invoice marked as sent');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const cancelInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to cancel this invoice?')) return;
    try {
      await api.put(`/finance/invoices/${invoiceId}/cancel`);
      toast.success('Invoice cancelled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const viewDetails = async (invoiceId) => {
    try {
      const res = await api.get(`/finance/invoices/${invoiceId}`);
      setViewInvoice(res.data);
    } catch {
      toast.error('Failed to load invoice');
    }
  };

  const downloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const res = await api.get(`/finance/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoiceNumber.replace(/\//g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const formatCurrency = (val) => `₹${(val || 0).toLocaleString('en-IN')}`;
  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  // Calculate totals for preview
  const baseAmount = parseFloat(form.base_amount) || 0;
  const roundOff = parseFloat(form.round_off) || 0;
  let gstAmount = 0;
  let igstVal = 0, cgstVal = 0, sgstVal = 0;
  
  if (useManualGST || form.is_manual_entry) {
    igstVal = parseFloat(form.manual_igst) || 0;
    cgstVal = parseFloat(form.manual_cgst) || 0;
    sgstVal = parseFloat(form.manual_sgst) || 0;
    gstAmount = igstVal + cgstVal + sgstVal;
  } else {
    gstAmount = baseAmount * 0.18;
    if (form.gst_type === 'igst') {
      igstVal = gstAmount;
    } else {
      cgstVal = baseAmount * 0.09;
      sgstVal = baseAmount * 0.09;
    }
  }
  const totalAmount = baseAmount + gstAmount + roundOff;

  return (
    <div data-testid="invoices-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">Invoices</h2>
          <p className="text-sm text-muted-foreground font-body mt-1">Manage billing invoices</p>
        </div>
        <Button onClick={openAdd} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="add-invoice-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Invoice
        </Button>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search invoices..." className="pl-10 rounded-sm" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 rounded-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="partially_paid">Partially Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-widest">Invoice #</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Client</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Date</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Due Date</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Amount</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Outstanding</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No invoices found</TableCell></TableRow>
            ) : filtered.map(inv => (
              <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                <TableCell className="font-mono text-sm font-medium">{inv.invoice_number}</TableCell>
                <TableCell>{inv.client_name}</TableCell>
                <TableCell className="text-sm">{inv.invoice_date}</TableCell>
                <TableCell className="text-sm">{inv.due_date}</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(inv.total_amount)}</TableCell>
                <TableCell className="text-right font-mono text-amber-600">{formatCurrency(inv.amount_outstanding)}</TableCell>
                <TableCell>
                  <Badge className={STATUS_COLORS[inv.status]}>{inv.status.replace('_', ' ')}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => viewDetails(inv.id)} title="View">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => downloadPdf(inv.id, inv.invoice_number)} title="Download PDF">
                      <Download className="w-4 h-4" />
                    </Button>
                    {!['paid', 'cancelled'].includes(inv.status) && (
                      <Button variant="ghost" size="sm" onClick={() => openEdit(inv)} title="Edit" className="text-blue-600">
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {inv.status === 'draft' && (
                      <Button variant="ghost" size="sm" onClick={() => markAsSent(inv.id)} className="text-emerald-600" title="Mark Sent">
                        <Send className="w-4 h-4" />
                      </Button>
                    )}
                    {!['paid', 'cancelled'].includes(inv.status) && inv.amount_paid === 0 && (
                      <Button variant="ghost" size="sm" onClick={() => cancelInvoice(inv.id)} className="text-red-600" title="Cancel">
                        <Ban className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Invoice Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl rounded-sm max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">{editMode ? 'Edit Invoice' : 'Create Invoice'}</DialogTitle>
            <DialogDescription>Fill in the invoice details below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 pr-2">
            {/* Manual Entry Toggle */}
            <div className="flex items-center gap-2 py-2 border-b border-border">
              <Switch checked={form.is_manual_entry} onCheckedChange={v => { set('is_manual_entry', v); setUseManualGST(v); }} />
              <span className="text-sm text-muted-foreground">Manual entry (for previously raised invoices)</span>
            </div>

            {/* Invoice Number for Manual Entry */}
            {(form.is_manual_entry || editMode) && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Invoice Number *</Label>
                <Input value={form.manual_invoice_number} onChange={e => set('manual_invoice_number', e.target.value)} className="rounded-sm font-mono" placeholder="HHIS/035/25-26" />
              </div>
            )}

            {/* Client Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Client *</Label>
                <Select value={form.client_id} onValueChange={handleClientChange} disabled={editMode}>
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.filter(c => c.is_active).map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.client_name}{c.state ? ` - ${c.state}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Invoice Type *</Label>
                <Select value={form.invoice_type} onValueChange={v => set('invoice_type', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="invoice-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recruitment">Recruitment</SelectItem>
                    <SelectItem value="survey">Survey</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Invoice Date *</Label>
                <Input type="date" value={form.invoice_date} onChange={e => set('invoice_date', e.target.value)} className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Due Date *</Label>
                <Input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} className="rounded-sm" />
              </div>
            </div>

            {/* SAC Code and LUT Info */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">SAC Code</Label>
                <Input value={form.sac_code} onChange={e => set('sac_code', e.target.value)} className="rounded-sm font-mono" placeholder="998519" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">LUT ARN No.</Label>
                <Input value={form.lut_arn_no} onChange={e => set('lut_arn_no', e.target.value)} className="rounded-sm font-mono" placeholder="AD0603240203210" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">LUT Date</Label>
                <Input value={form.lut_arn_date} onChange={e => set('lut_arn_date', e.target.value)} className="rounded-sm" placeholder="12/03/2024" />
              </div>
            </div>

            {/* Line Items */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Line Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="rounded-sm">
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
              <div className="border border-border rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="p-2 text-left text-xs uppercase w-16">S.No</th>
                      <th className="p-2 text-left text-xs uppercase">Description</th>
                      <th className="p-2 text-left text-xs uppercase">{form.invoice_type === 'survey' ? 'No. of Surveys' : 'Candidate Name'}</th>
                      <th className="p-2 text-left text-xs uppercase">{form.invoice_type === 'survey' ? 'Price' : 'DOJ'}</th>
                      <th className="p-2 text-right text-xs uppercase">Total</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.line_items.map((item, idx) => (
                      <tr key={item._key || idx} className="border-t border-border">
                        <td className="p-1 text-center font-mono text-muted-foreground">
                          {idx + 1}
                        </td>
                        <td className="p-1">
                          <Input value={item.description} onChange={e => updateLineItem(idx, 'description', e.target.value)} className="rounded-sm" placeholder={form.invoice_type === 'survey' ? 'Survey services' : 'Manpower Recruitment and supply agency services'} />
                        </td>
                        <td className="p-1">
                          {form.invoice_type === 'survey' ? (
                            <Input type="number" value={item.num_surveys} onChange={e => updateLineItem(idx, 'num_surveys', e.target.value)} className="rounded-sm" placeholder="0" />
                          ) : (
                            <Input value={item.candidate_name} onChange={e => updateLineItem(idx, 'candidate_name', e.target.value)} className="rounded-sm" placeholder="Candidate name" />
                          )}
                        </td>
                        <td className="p-1">
                          {form.invoice_type === 'survey' ? (
                            <Input type="number" value={item.price} onChange={e => updateLineItem(idx, 'price', e.target.value)} className="rounded-sm text-right font-mono" placeholder="0" />
                          ) : (
                            <Input type="date" value={item.date_of_joining} onChange={e => updateLineItem(idx, 'date_of_joining', e.target.value)} className="rounded-sm" />
                          )}
                        </td>
                        <td className="p-1">
                          <Input type="number" value={item.amount} onChange={e => updateLineItem(idx, 'amount', e.target.value)} className="rounded-sm text-right font-mono" placeholder="0" />
                        </td>
                        <td className="p-1">
                          {form.line_items.length > 1 && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem(idx)} className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* GST Options */}
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <div className="space-y-1.5 flex-1">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">GST Type</Label>
                  <Select value={form.gst_type} onValueChange={v => set('gst_type', v)} disabled={useManualGST}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="igst">IGST (18%) - Inter-state</SelectItem>
                      <SelectItem value="cgst_sgst">CGST + SGST (9% + 9%) - Haryana</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch checked={useManualGST} onCheckedChange={setUseManualGST} />
                  <span className="text-xs text-muted-foreground">Manual GST</span>
                </div>
              </div>

              {/* Manual GST Inputs */}
              {useManualGST && (
                <div className="grid grid-cols-3 gap-4 p-3 bg-muted/50 rounded-sm">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">IGST Amount</Label>
                    <Input type="number" value={form.manual_igst} onChange={e => set('manual_igst', e.target.value)} className="rounded-sm font-mono" placeholder="0" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">CGST Amount</Label>
                    <Input type="number" value={form.manual_cgst} onChange={e => set('manual_cgst', e.target.value)} className="rounded-sm font-mono" placeholder="0" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">SGST Amount</Label>
                    <Input type="number" value={form.manual_sgst} onChange={e => set('manual_sgst', e.target.value)} className="rounded-sm font-mono" placeholder="0" />
                  </div>
                </div>
              )}
            </div>

            {/* Round Off and Already Paid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Round Off</Label>
                <Input type="number" value={form.round_off} onChange={e => set('round_off', e.target.value)} className="rounded-sm font-mono" placeholder="0" step="0.01" />
              </div>
              {form.is_manual_entry && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Amount Already Paid</Label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input type="number" value={form.amount_already_paid} onChange={e => set('amount_already_paid', e.target.value)} className="pl-10 rounded-sm font-mono" placeholder="0" />
                  </div>
                </div>
              )}
            </div>

            {/* Amount Summary */}
            {baseAmount > 0 && (
              <div className="bg-muted/50 p-4 rounded-sm text-sm space-y-2">
                <div className="flex justify-between"><span>Subtotal:</span><span className="font-mono">{formatCurrency(baseAmount)}</span></div>
                {igstVal > 0 && (
                  <div className="flex justify-between text-muted-foreground"><span>IGST (18%):</span><span className="font-mono">{formatCurrency(igstVal)}</span></div>
                )}
                {cgstVal > 0 && (
                  <>
                    <div className="flex justify-between text-muted-foreground"><span>CGST (9%):</span><span className="font-mono">{formatCurrency(cgstVal)}</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>SGST (9%):</span><span className="font-mono">{formatCurrency(sgstVal)}</span></div>
                  </>
                )}
                {roundOff !== 0 && (
                  <div className="flex justify-between text-muted-foreground"><span>Round Off:</span><span className="font-mono">{formatCurrency(roundOff)}</span></div>
                )}
                <div className="flex justify-between font-medium border-t border-border pt-2 text-base">
                  <span>Total:</span>
                  <span className="font-mono">{formatCurrency(totalAmount)}</span>
                </div>
              </div>
            )}

            {/* Description */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Description / Notes</Label>
              <Textarea value={form.description} onChange={e => set('description', e.target.value)} className="rounded-sm" rows={2} placeholder="Invoice description" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 rounded-sm">
              {saving ? 'Saving...' : (editMode ? 'Update Invoice' : 'Create Invoice')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Invoice Dialog */}
      <Dialog open={!!viewInvoice} onOpenChange={() => setViewInvoice(null)}>
        <DialogContent className="max-w-2xl rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Invoice {viewInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {viewInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground uppercase tracking-widest">Client</p>
                  <p className="font-medium">{viewInvoice.client?.client_name}</p>
                  <p className="text-sm text-muted-foreground">{viewInvoice.client?.billing_address}</p>
                  <p className="text-sm font-mono">{viewInvoice.client?.gst_number}</p>
                </div>
                <div className="space-y-2 text-right">
                  <Badge className={STATUS_COLORS[viewInvoice.status]}>{viewInvoice.status.replace('_', ' ')}</Badge>
                  <p className="text-sm"><span className="text-muted-foreground">Invoice Date:</span> {viewInvoice.invoice_date}</p>
                  <p className="text-sm"><span className="text-muted-foreground">Due Date:</span> {viewInvoice.due_date}</p>
                  {viewInvoice.sac_code && <p className="text-sm font-mono"><span className="text-muted-foreground">SAC:</span> {viewInvoice.sac_code}</p>}
                </div>
              </div>

              {viewInvoice.line_items?.length > 0 && (
                <div className="border border-border rounded-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="p-2 text-left w-16">S.No</th>
                        <th className="p-2 text-left">Description</th>
                        <th className="p-2 text-left">{viewInvoice.invoice_type === 'survey' ? 'No. of Surveys' : 'Candidate Name'}</th>
                        <th className="p-2 text-left">{viewInvoice.invoice_type === 'survey' ? 'Price' : 'DOJ'}</th>
                        <th className="p-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewInvoice.line_items.map((item, idx) => (
                        <tr key={item._key || `view-${idx}`} className="border-t border-border">
                          <td className="p-2 text-center font-mono">{idx + 1}</td>
                          <td className="p-2">{item.description}</td>
                          <td className="p-2">{viewInvoice.invoice_type === 'survey' ? item.num_surveys : item.candidate_name}</td>
                          <td className="p-2">{viewInvoice.invoice_type === 'survey' ? (item.price ? formatCurrency(item.price) : '') : item.date_of_joining}</td>
                          <td className="p-2 text-right font-mono">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="border-t border-b border-border py-4 space-y-2">
                <p className="text-sm text-muted-foreground">{viewInvoice.description}</p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-muted-foreground">Base Amount</span><span className="font-mono">{formatCurrency(viewInvoice.base_amount)}</span></div>
                {viewInvoice.igst > 0 && (
                  <div className="flex justify-between"><span className="text-muted-foreground">IGST (18%)</span><span className="font-mono">{formatCurrency(viewInvoice.igst)}</span></div>
                )}
                {viewInvoice.cgst > 0 && (
                  <>
                    <div className="flex justify-between"><span className="text-muted-foreground">CGST (9%)</span><span className="font-mono">{formatCurrency(viewInvoice.cgst)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">SGST (9%)</span><span className="font-mono">{formatCurrency(viewInvoice.sgst)}</span></div>
                  </>
                )}
                {viewInvoice.round_off !== 0 && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Round Off</span><span className="font-mono">{formatCurrency(viewInvoice.round_off)}</span></div>
                )}
                <div className="flex justify-between font-medium border-t border-border pt-2">
                  <span>Total Amount</span>
                  <span className="font-mono text-lg">{formatCurrency(viewInvoice.total_amount)}</span>
                </div>
                <div className="flex justify-between text-emerald-600">
                  <span>Amount Paid</span>
                  <span className="font-mono">{formatCurrency(viewInvoice.amount_paid)}</span>
                </div>
                <div className="flex justify-between text-amber-600 font-medium">
                  <span>Outstanding</span>
                  <span className="font-mono">{formatCurrency(viewInvoice.amount_outstanding)}</span>
                </div>
              </div>

              {viewInvoice.payments?.length > 0 && (
                <div className="border-t border-border pt-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Payment History</p>
                  {viewInvoice.payments.map(p => (
                    <div key={p.id} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                      <div>
                        <p className="text-sm">{p.payment_date}</p>
                        <p className="text-xs text-muted-foreground">{p.payment_mode} • {p.reference_number}</p>
                      </div>
                      <span className="font-mono text-emerald-600">{formatCurrency(p.amount_received)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => downloadPdf(viewInvoice.id, viewInvoice.invoice_number)} className="rounded-sm">
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
